import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../components/api/axiosInstance";
import { getAuthItem } from "../utils/authStorage";
import { FaChevronLeft, FaChevronRight, FaPlus, FaRegCalendarAlt, FaTimes } from "react-icons/fa";
import { mapCustomerListItemFromApi, getCustomerSelectLabel } from "../utils/formatters";
import { staffDmApi } from "./dm/dmApi";
import { statusClass } from "./MyDay";
import "../assets/css/MyDay.css";

const CONTENT_TYPES = ["Text", "Image", "Video", "Reel", "Carousel", "Story", "Link"];

const QUICK_ACTIONS = [
  { label: "Review", status: "Review" },
  { label: "Approve", status: "Approved" },
  { label: "Posted", status: "Posted" },
  { label: "Skip", status: "Skipped" },
];

const PLATFORM_COLORS = {
  facebook: "#1877f2",
  instagram: "#d62976",
  linkedin: "#0a66c2",
  youtube: "#ff0000",
  twitter: "#0f172a",
  x: "#0f172a",
  gmb: "#ea4335",
  google: "#ea4335",
  website: "#0d9488",
  whatsapp: "#25d366",
  pinterest: "#e60023",
};

function platformColor(name) {
  const key = String(name || "").trim().toLowerCase();
  if (PLATFORM_COLORS[key]) return PLATFORM_COLORS[key];
  const match = Object.keys(PLATFORM_COLORS).find((k) => k.length > 2 && key.includes(k));
  return match ? PLATFORM_COLORS[match] : "#64748b";
}

function toISODateLocal(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function mondayOf(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  return x;
}

function addDays(d, n) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() + n);
  return x;
}

function formatWeekRange(start) {
  const end = addDays(start, 6);
  const sameYear = start.getFullYear() === end.getFullYear();
  const startText = start.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const endText = end.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return `${startText} – ${endText}`;
}

function getLoggedInUserId() {
  try {
    const user = JSON.parse(getAuthItem("user") || "{}");
    return user.user_id ?? user.id ?? user.User_Id ?? "";
  } catch {
    return "";
  }
}

function emptyForm(weekStartISO) {
  return {
    tenant_id: "",
    Dm_Project_Id: "",
    Social_Account_Id: "",
    Planned_Date: weekStartISO || toISODateLocal(),
    Planned_Time: "",
    Content_Type: "Image",
    Caption: "",
    Media_URL: "",
    External_Link: "",
    Assigned_To: String(getLoggedInUserId() || ""),
    Status_Id: "",
    Notes: "",
  };
}

function DMCalendar({
  title = "Digital Marketing",
  subtitle = null,
}) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [posts, setPosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [filterTenant, setFilterTenant] = useState("");
  const [filterProject, setFilterProject] = useState("");
  const [dmProjects, setDmProjects] = useState([]);
  const [includeInactiveTenants, setIncludeInactiveTenants] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(() => emptyForm(toISODateLocal(mondayOf(new Date()))));
  const [saving, setSaving] = useState(false);

  const weekStartISO = toISODateLocal(weekStart);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const weekEnd = toISODateLocal(addDays(weekStart, 6));
      const params = { date_from: weekStartISO, date_to: weekEnd };
      if (filterTenant) params.tenant_id = filterTenant;
      if (filterProject) params.dm_project_id = filterProject;
      const tenantParams = includeInactiveTenants
        ? { include_inactive: "1" }
        : { status: "Active" };
      const skipped = Promise.resolve({ data: null });
      const [postsRes, summaryRes, statusRes, tenantsRes, usersRes] = await Promise.all([
        filterTenant ? axiosInstance.get("/dm-posts", { params }) : skipped,
        filterTenant
          ? axiosInstance.get("/dm-posts/week-summary", { params: { week_start: weekStartISO, tenant_id: filterTenant } })
          : skipped,
        axiosInstance.get("/dm-post-statuses"),
        axiosInstance.get("/clients", { params: tenantParams }),
        axiosInstance.get("/users", { params: { status: "Active" } }),
      ]);
      setPosts(Array.isArray(postsRes.data) ? postsRes.data : []);
      setSummary(summaryRes.data || null);
      setStatuses(Array.isArray(statusRes.data) ? statusRes.data : []);
      const rawTenants = Array.isArray(tenantsRes.data)
        ? tenantsRes.data
        : Array.isArray(tenantsRes.data?.data)
          ? tenantsRes.data.data
          : [];
      const mapped = rawTenants.map(mapCustomerListItemFromApi).filter(Boolean);
      // Client-side guard: Active only unless include-inactive is on
      const nextTenants = includeInactiveTenants
        ? mapped
        : mapped.filter((row) => String(row.Status || row.status || "").trim().toLowerCase() === "active");
      setTenants(nextTenants);
      if (filterTenant) {
        const stillVisible = nextTenants.some(
          (row) => String(row.id ?? row.client_id ?? "") === String(filterTenant)
        );
        if (!stillVisible) setFilterTenant("");
      }
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      const draft = (statusRes.data || []).find((s) => String(s.Status).toLowerCase() === "draft");
      setForm((prev) => ({
        ...prev,
        Status_Id: prev.Status_Id || (draft ? String(draft.Status_Id) : ""),
        Planned_Date: prev.Planned_Date || weekStartISO,
      }));
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to load DM calendar");
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekStartISO, filterTenant, filterProject, includeInactiveTenants]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    staffDmApi
      .listProjects()
      .then((rows) => {
        if (!cancelled) setDmProjects(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setDmProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const projectsForTenant = useCallback(
    (tenantId) => (tenantId ? dmProjects.filter((p) => String(p.tenant_id) === String(tenantId)) : dmProjects),
    [dmProjects]
  );

  useEffect(() => {
    if (filterProject && !projectsForTenant(filterTenant).some((p) => String(p.Dm_Project_Id) === filterProject)) {
      setFilterProject("");
    }
  }, [filterTenant, filterProject, projectsForTenant]);

  useEffect(() => {
    let cancelled = false;
    const loadSocial = async () => {
      if (!form.tenant_id) {
        setSocialAccounts([]);
        return;
      }
      try {
        const res = await axiosInstance.get(`/social-accounts/client/${form.tenant_id}`);
        if (!cancelled) setSocialAccounts(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setSocialAccounts([]);
      }
    };
    loadSocial();
    return () => { cancelled = true; };
  }, [form.tenant_id]);

  const postsByDate = useMemo(() => {
    const map = {};
    for (const p of posts) {
      const key = p.Planned_Date;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    }
    return map;
  }, [posts]);

  const tenantLabel = (t) => getCustomerSelectLabel(t);
  const tenantIdOf = (t) => t?.id ?? t?.client_id ?? t?.Customer_Id ?? t?.tenant_id ?? "";

  const clientOptions = useMemo(
    () =>
      [...tenants].sort((a, b) =>
        tenantLabel(a).localeCompare(tenantLabel(b), undefined, { sensitivity: "base" })
      ),
    [tenants]
  );

  const renderPartyOptions = () =>
    clientOptions.map((t) => (
      <option key={tenantIdOf(t)} value={tenantIdOf(t)}>
        {tenantLabel(t)}
      </option>
    ));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.tenant_id || !form.Planned_Date) {
      alert("Client and planned date are required.");
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.post("/dm-posts", {
        tenant_id: Number(form.tenant_id),
        Dm_Project_Id: form.Dm_Project_Id ? Number(form.Dm_Project_Id) : null,
        Social_Account_Id: form.Social_Account_Id ? Number(form.Social_Account_Id) : null,
        Planned_Date: form.Planned_Date,
        Planned_Time: form.Planned_Time || null,
        Content_Type: form.Content_Type,
        Caption: form.Caption || null,
        Media_URL: form.Media_URL || null,
        External_Link: form.External_Link || null,
        Assigned_To: form.Assigned_To ? Number(form.Assigned_To) : null,
        Status_Id: form.Status_Id ? Number(form.Status_Id) : null,
        Notes: form.Notes || null,
      });
      setShowForm(false);
      setForm(emptyForm(weekStartISO));
      await load();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to create post");
    } finally {
      setSaving(false);
    }
  };

  const quickStatus = async (post, statusName) => {
    const status = statuses.find((s) => String(s.Status).toLowerCase() === statusName.toLowerCase());
    if (!status) return;
    const payload = { Status_Id: status.Status_Id };
    if (statusName.toLowerCase() === "posted") {
      const url = window.prompt("Paste live post URL:") || "";
      if (url) payload.Posted_URL = url;
    }
    if (statusName.toLowerCase() === "skipped") {
      const reason = window.prompt("Skip reason:");
      if (!reason) return;
      payload.Skip_Reason = reason;
    }
    try {
      await axiosInstance.put(`/dm-posts/${post.Post_Id}`, payload);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update");
    }
  };

  const isCurrentWeek = weekStartISO === toISODateLocal(mondayOf(new Date()));
  const todayISO = toISODateLocal();

  return (
    <div className="myday-page">
      <div className="myday-toolbar dmcal-head">
        <div className="myday-toolbar__left">
          <h2 className="myday-title">{title}</h2>
          {subtitle ? <p className="myday-subtitle">{subtitle}</p> : null}
        </div>
      </div>

      <div className="dmcal-bar">
        <div className="dmcal-week" role="group" aria-label="Week">
          <button
            type="button"
            className="dmcal-week__nav"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            aria-label="Previous week"
            title="Previous week"
          >
            <FaChevronLeft aria-hidden="true" />
          </button>
          <div className="dmcal-week__range">
            <FaRegCalendarAlt aria-hidden="true" />
            <span>{formatWeekRange(weekStart)}</span>
          </div>
          <button
            type="button"
            className="dmcal-week__nav"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            aria-label="Next week"
            title="Next week"
          >
            <FaChevronRight aria-hidden="true" />
          </button>
        </div>
        <div className="dmcal-bar__actions">
          <button
            type="button"
            className="myday-btn myday-btn--secondary dmcal-btn"
            onClick={() => setWeekStart(mondayOf(new Date()))}
            disabled={isCurrentWeek}
          >
            This week
          </button>
          <button
            type="button"
            className="myday-btn dmcal-btn"
            onClick={() => {
              if (!showForm && filterTenant) setForm((p) => (p.tenant_id ? p : { ...p, tenant_id: filterTenant }));
              setShowForm((v) => !v);
            }}
          >
            {showForm ? <FaTimes aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
            <span>{showForm ? "Close" : "Add post"}</span>
          </button>
        </div>
      </div>

      <div className="dmcal-filters">
        <label className="dmcal-field">
          <span className="dmcal-field__label">Client</span>
          <select value={filterTenant} onChange={(e) => setFilterTenant(e.target.value)}>
            <option value="">Select a client…</option>
            {renderPartyOptions()}
          </select>
        </label>
        <label className="dmcal-field">
          <span className="dmcal-field__label">Project</span>
          <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
            <option value="">All projects</option>
            {projectsForTenant(filterTenant).map((p) => (
              <option key={p.Dm_Project_Id} value={p.Dm_Project_Id}>
                {filterTenant ? p.Project_Name : `${p.Project_Name} — ${p.Tenant_Name}`}
              </option>
            ))}
          </select>
        </label>
        <label className="dmcal-switch">
          <input
            type="checkbox"
            checked={includeInactiveTenants}
            onChange={(e) => setIncludeInactiveTenants(e.target.checked)}
          />
          <span className="dmcal-switch__track" aria-hidden="true" />
          <span className="dmcal-switch__text">Show inactive</span>
        </label>
      </div>

      {summary?.clients?.length ? (
        <div className="dm-summary">
          {summary.clients.map((c) => (
            <div key={c.tenant_id} className="dm-summary__card">
              <strong>{c.Client_Name}</strong>
              <span>{c.posted}/{c.planned} posted</span>
              <span>{c.open} open</span>
            </div>
          ))}
        </div>
      ) : null}

      {showForm ? (
        <form className="dm-form" onSubmit={handleSave}>
          <div className="dm-form__grid">
            <label>
              Client *
              <select
                required
                value={form.tenant_id}
                onChange={(e) =>
                  setForm((p) => ({ ...p, tenant_id: e.target.value, Dm_Project_Id: "", Social_Account_Id: "" }))
                }
              >
                <option value="">Select…</option>
                {renderPartyOptions()}
              </select>
            </label>
            <label>
              DM project
              <select
                value={form.Dm_Project_Id}
                disabled={!form.tenant_id}
                onChange={(e) => setForm((p) => ({ ...p, Dm_Project_Id: e.target.value }))}
              >
                <option value="">None</option>
                {(form.tenant_id ? projectsForTenant(form.tenant_id) : []).map((p) => (
                  <option key={p.Dm_Project_Id} value={p.Dm_Project_Id}>
                    {p.Project_Name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Social account
              <select
                value={form.Social_Account_Id}
                onChange={(e) => setForm((p) => ({ ...p, Social_Account_Id: e.target.value }))}
              >
                <option value="">Optional</option>
                {socialAccounts.map((a) => (
                  <option key={a.Account_Id} value={a.Account_Id}>
                    {a.Platform_Name} — {a.Account_Name || a.Username}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Planned date *
              <input
                type="date"
                required
                value={form.Planned_Date}
                onChange={(e) => setForm((p) => ({ ...p, Planned_Date: e.target.value }))}
              />
            </label>
            <label>
              Time
              <input
                type="time"
                value={form.Planned_Time}
                onChange={(e) => setForm((p) => ({ ...p, Planned_Time: e.target.value }))}
              />
            </label>
            <label>
              Content type
              <select
                value={form.Content_Type}
                onChange={(e) => setForm((p) => ({ ...p, Content_Type: e.target.value }))}
              >
                {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>
              Status
              <select
                value={form.Status_Id}
                onChange={(e) => setForm((p) => ({ ...p, Status_Id: e.target.value }))}
              >
                {statuses.map((s) => (
                  <option key={s.Status_Id} value={s.Status_Id}>{s.Status}</option>
                ))}
              </select>
            </label>
            <label>
              Assign to
              <select
                value={form.Assigned_To}
                onChange={(e) => setForm((p) => ({ ...p, Assigned_To: e.target.value }))}
              >
                <option value="">Unassigned</option>
                {users.map((u) => {
                  const id = u.user_id ?? u.User_Id;
                  const name = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || id;
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>
            </label>
            <label className="dm-form__full">
              Caption / text
              <textarea
                rows={3}
                value={form.Caption}
                onChange={(e) => setForm((p) => ({ ...p, Caption: e.target.value }))}
              />
            </label>
            <label>
              Media URL
              <input
                type="url"
                value={form.Media_URL}
                onChange={(e) => setForm((p) => ({ ...p, Media_URL: e.target.value }))}
              />
            </label>
            <label>
              External link
              <input
                type="url"
                value={form.External_Link}
                onChange={(e) => setForm((p) => ({ ...p, External_Link: e.target.value }))}
              />
            </label>
          </div>
          <div className="dm-form__actions">
            <button type="submit" className="myday-btn" disabled={saving}>{saving ? "Saving…" : "Save post"}</button>
          </div>
        </form>
      ) : null}

      {!filterTenant ? (
        <div className="dmcal-empty">
          <div className="dmcal-empty__icon" aria-hidden="true">
            <FaRegCalendarAlt />
          </div>
          <div className="dmcal-empty__title">No client selected</div>
          <p className="dmcal-empty__text">Choose a client above to see and plan this week's social posts.</p>
        </div>
      ) : loading ? (
        <div className="myday-empty">Loading…</div>
      ) : error ? (
        <div className="myday-empty myday-empty--error">{error}</div>
      ) : (
        <div className="dm-week">
          {days.map((d) => {
            const key = toISODateLocal(d);
            const dayPosts = postsByDate[key] || [];
            const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
            return (
              <div key={key} className={`dm-day${key === todayISO ? " dm-day--today" : ""}`}>
                <div className="dm-day__head">
                  <strong>{weekday}</strong>
                  <span>{key}</span>
                  <span className="dm-day__count">{dayPosts.length}</span>
                </div>
                <div className="dm-day__body">
                  {dayPosts.length === 0 ? (
                    <div className="dm-day__empty">No posts</div>
                  ) : (
                    dayPosts.map((p) => {
                      const platform = p.Platform || p.Platform_Name || "";
                      const currentStatus = String(p.Status || "").toLowerCase();
                      const showProject =
                        p.Project_Name &&
                        String(p.Project_Name).trim().toLowerCase() !== String(p.Client_Name || "").trim().toLowerCase();
                      return (
                        <div
                          key={p.Post_Id}
                          className="dm-post-chip"
                          style={{ "--dm-platform": platformColor(platform) }}
                        >
                          <div className="dm-post-chip__top">
                            <span className="dm-post-chip__platform">
                              <span className="dm-post-chip__dot" aria-hidden="true" />
                              {platform || "No platform"}
                              {p.Content_Type ? <span className="dm-post-chip__ctype">· {p.Content_Type}</span> : null}
                            </span>
                            <span className={`myday-pill ${statusClass(p.Status)}`}>{p.Status || "—"}</span>
                          </div>
                          <div className="dm-post-chip__client">
                            {p.Client_Name}
                            {showProject ? <span className="dm-post-chip__project"> · {p.Project_Name}</span> : null}
                          </div>
                          <div className="dm-post-chip__caption">{p.Caption || "Untitled"}</div>
                          <div className="dm-post-chip__actions" role="group" aria-label="Update status">
                            {QUICK_ACTIONS.map(({ label, status }) => (
                              <button
                                key={status}
                                type="button"
                                disabled={currentStatus === status.toLowerCase()}
                                onClick={() => quickStatus(p, status)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DMCalendar;
